import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper,
  Grid,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { countXLRConnectors, getFinishingPricingSync, getHeatShrinkEndPrice } from '../services/pricingService';
import { compatibilityMatrix } from '../services/compatibilityMatrix';
import CustomHeatShrinkPanel from './CustomHeatShrinkPanel';
import { countCustomHeatShrinkEnds } from '../utils/customHeatShrinkHelpers';

/**
 * SimplifiedFinishingSelection - Streamlined component for selecting finishing options
 * 
 * @param {Object} props
 * @param {Object} props.value - Current finishing selection value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Object} props.config - Current cable configuration to check for XLR connectors
 */
const defaultCustomHs = () => ({
  mode: 'text',
  attestedGrayscale: false,
  logo: { s3Key: '', publicUrl: '' },
  line1: '',
  line2: '',
  tubeColor: 'black',
  ribbonColor: 'white'
});

function SimplifiedFinishingSelection({ value = {}, onChange, config = {}, sku = '' }) {
  // Handle heat shrink - support both old format (single type) and new format (per-connector)
  const normalizeHeatShrink = (heatShrink) => {
    if (!heatShrink) return { global: 'none' };
    if (typeof heatShrink === 'string') {
      return { global: heatShrink };
    }
    if (typeof heatShrink === 'object' && heatShrink.type) {
      return { global: heatShrink.type };
    }
    return { ...heatShrink };
  };
  
  // Handle coding rings - support both old format (single color) and new format (per-connector)
  const normalizeCodingRings = (codingRings) => {
    if (!codingRings) return { global: 'none' };
    if (typeof codingRings === 'string') {
      return { global: codingRings };
    }
    return { ...codingRings };
  };
  
  const [heatShrink, setHeatShrink] = useState(normalizeHeatShrink(value.heatShrink));
  const [codingRings, setCodingRings] = useState(normalizeCodingRings(value.codingRings));
  const [customHeatShrink, setCustomHeatShrink] = useState(value.customHeatShrink || defaultCustomHs());

  const valueRef = useRef(value);
  valueRef.current = value;

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (value.customHeatShrink) {
      setCustomHeatShrink({ ...defaultCustomHs(), ...value.customHeatShrink });
    } else {
      setCustomHeatShrink(defaultCustomHs());
    }
  }, [value.customHeatShrink]);

  // Coding ring color options
  const codingRingColors = [
    { value: 'none', label: 'Black is Standard' },
    { value: 'brown', label: 'Brown' },
    { value: 'red', label: 'Red' },
    { value: 'orange', label: 'Orange' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'green', label: 'Green' },
    { value: 'blue', label: 'Blue' },
    { value: 'violet', label: 'Violet' },
    { value: 'grey', label: 'Grey' },
    { value: 'white', label: 'White' }
  ];

  // Helper function to identify XLR connectors
  const isXLRConnector = (conn) => {
    if (!conn) return false;
    
    return (
      (conn.connector && (
        conn.connector.includes('XLR') ||
        conn.connector.includes('XL') ||
        conn.connector.includes('NC3') ||
        conn.connector.includes('NC5')
      )) ||
      (conn.skuCode && (
        conn.skuCode.includes('XLR') ||
        conn.skuCode.includes('XL') ||
        conn.skuCode.includes('NC3') ||
        conn.skuCode.includes('NC5')
      )) ||
      (conn.id && (
        conn.id.includes('XLR') ||
        conn.id.includes('XL') ||
        conn.id.includes('NC3') ||
        conn.id.includes('NC5')
      )) ||
      (conn.family === 'XLR')
    );
  };

  // Helper function to get connector display name
  const getConnectorDisplayName = (conn, position) => {
    if (!conn) return position;
    
    let name = 'Unknown connector';
    
    if (conn.id) {
      // Get the proper description from the compatibility matrix
      const matrixEntry = compatibilityMatrix[conn.id];
      if (matrixEntry?.description) {
        name = matrixEntry.description;
      } else if (matrixEntry?.name) {
        name = matrixEntry.name;
      } else if (conn.description) {
        name = conn.description;
      } else if (conn.label) {
        name = conn.label;
      } else if (conn.name) {
        name = conn.name;
      } else {
        name = conn.id;
      }
    } else {
      // Fallback for connectors without ID
      name = conn.name || conn.description || conn.label || conn.id || conn.skuCode || conn.connector || 'Unknown connector';
    }
    
    return `${position}: ${name}`;
  };

  // Helper function to get all XLR connectors from config
  const getXLRConnectors = (config) => {
    const xlrConnectors = [];
    
    // Check standard connectors
    if (config.connectors?.endA && isXLRConnector(config.connectors.endA)) {
      xlrConnectors.push({
        key: 'endA',
        position: 'End A',
        connector: config.connectors.endA
      });
    }
    
    if (config.connectors?.endB && isXLRConnector(config.connectors.endB)) {
      xlrConnectors.push({
        key: 'endB',
        position: 'End B',
        connector: config.connectors.endB
      });
    }
    
    // Check hybrid connectors
    if (config.hybridConnectors?.connectors) {
      Object.entries(config.hybridConnectors.connectors).forEach(([key, connector]) => {
        if (connector && isXLRConnector(connector)) {
          xlrConnectors.push({
            key: key,
            position: `Connector ${key}`,
            connector: connector
          });
        }
      });
    }
    
    // Check wye connectors
    if (config.wyeConnectors?.connectors) {
      Object.entries(config.wyeConnectors.connectors).forEach(([key, connector]) => {
        if (connector && isXLRConnector(connector)) {
          xlrConnectors.push({
            key: key,
            position: `Connector ${key}`,
            connector: connector
          });
        }
      });
    }
    
    // Check zip connectors
    if (config.zipConnectors?.connectors) {
      Object.entries(config.zipConnectors.connectors).forEach(([key, connector]) => {
        if (connector && isXLRConnector(connector)) {
          xlrConnectors.push({
            key: key,
            position: `Connector ${key}`,
            connector: connector
          });
        }
      });
    }
    
    return xlrConnectors;
  };

  // Get all connectors for heat shrink options
  const getAllConnectors = (config) => {
    const allConnectors = [];
    
    // Check if this is a hybrid/zip/wye cable
    const isHybridCable = config.style && ['hybrid', 'hybrid-power-audio', 'hybrid-power-audio-audio', 'zip', 'zip-audio-audio', 'wye'].includes(config.style);
    
    // Only add standard connectors (endA/endB) if this is NOT a hybrid/zip/wye cable
    if (!isHybridCable) {
      if (config.connectors?.endA) {
        allConnectors.push({
          key: 'endA',
          position: 'End A',
          connector: config.connectors.endA
        });
      }
      if (config.connectors?.endB) {
        allConnectors.push({
          key: 'endB',
          position: 'End B',
          connector: config.connectors.endB
        });
      }
    }
    
    // Add hybrid connectors
    if (config.hybridConnectors?.connectors) {
      Object.entries(config.hybridConnectors.connectors).forEach(([key, connector]) => {
        if (connector) {
          allConnectors.push({
            key: key,
            position: `Connector ${key}`,
            connector: connector
          });
        }
      });
    }
    
    // Add wye connectors
    if (config.wyeConnectors?.connectors) {
      Object.entries(config.wyeConnectors.connectors).forEach(([key, connector]) => {
        if (connector) {
          allConnectors.push({
            key: key,
            position: `Connector ${key}`,
            connector: connector
          });
        }
      });
    }
    
    // Add zip connectors
    if (config.zipConnectors?.connectors) {
      Object.entries(config.zipConnectors.connectors).forEach(([key, connector]) => {
        if (connector) {
          allConnectors.push({
            key: key,
            position: `Connector ${key}`,
            connector: connector
          });
        }
      });
    }
    
    return allConnectors;
  };

  // Check if any XLR connector is selected
  const hasXLRConnector = () => {
    const xlrConnectors = getXLRConnectors(config);
    return xlrConnectors.length > 0;
  };

  // Update parent when local state changes
  useEffect(() => {
    const anyCustom = Object.entries(heatShrink).some(
      ([k, v]) => k !== 'global' && (v === 'custom' || v === 'clear+custom')
    );
    const next = {
      ...valueRef.current,
      heatShrink,
      codingRings,
      ...(anyCustom ? { customHeatShrink } : { customHeatShrink: undefined })
    };

    // Avoid render loops: only emit when values actually differ.
    const prev = valueRef.current || {};
    const sameHeatShrink = JSON.stringify(prev.heatShrink || {}) === JSON.stringify(next.heatShrink || {});
    const sameCodingRings = JSON.stringify(prev.codingRings || {}) === JSON.stringify(next.codingRings || {});
    const sameCustomHs = JSON.stringify(prev.customHeatShrink || null) === JSON.stringify(next.customHeatShrink || null);
    if (sameHeatShrink && sameCodingRings && sameCustomHs) return;

    onChangeRef.current(next);
  }, [heatShrink, codingRings, customHeatShrink]);

  // Handle heat shrink change for a specific connector
  const handleHeatShrinkChange = (connectorKey, heatShrinkType) => {
    const newHeatShrink = { ...heatShrink };
    newHeatShrink[connectorKey] = heatShrinkType;
    setHeatShrink(newHeatShrink);
  };

  // Handle coding ring change for a specific connector
  const handleCodingRingChange = (connectorKey, color) => {
    const newCodingRings = { ...codingRings };
    newCodingRings[connectorKey] = color;
    setCodingRings(newCodingRings);
  };

  const xlrConnectors = getXLRConnectors(config);
  const allConnectors = getAllConnectors(config);
  
  // Calculate total heat shrink cost
  let heatShrinkTotal = 0;
  allConnectors.forEach(({ key }) => {
    const heatShrinkType = heatShrink[key] || heatShrink.global || 'none';
    heatShrinkTotal += getHeatShrinkEndPrice(heatShrinkType);
  });
  
  // Calculate total coding rings cost
  let codingRingsTotal = 0;
  xlrConnectors.forEach(({ key }) => {
    const color = codingRings[key] || codingRings.global || 'none';
    const unitPrice = getFinishingPricingSync('codingRings', color);
    codingRingsTotal += unitPrice;
  });

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Finishing Options
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={3}>
          {/* Heat Shrink Option */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mr: 1 }}>
                Heat Shrink
              </Typography>
              <Tooltip title="Add-on heat shrink on the jacket above the plug; clear, custom printed, or both per end">
                <HelpOutlineIcon color="action" fontSize="small" />
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              NLFX branding stays at each connector; optional add-on tubing sits on the jacket above the plug.
              Choose per connector. <i>(${heatShrinkTotal.toFixed(2)} total)</i>
              <br />
              <i>
                Clear: unshrunk, no printing. Custom printed: text or logo (colors in the panel). Clear + custom on
                one end charges both add-ons.
              </i>
            </Typography>
            
            {allConnectors.map(({ key, position, connector }, index) => (
              <Box key={key} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {getConnectorDisplayName(connector, position)}
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Heat Shrink</InputLabel>
                  <Select
                    value={heatShrink[key] || heatShrink.global || 'none'}
                    onChange={(e) => handleHeatShrinkChange(key, e.target.value)}
                    label="Heat Shrink"
                    MenuProps={{ disablePortal: true }}
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="clear">2&quot; clear (plain, unshrunk)</MenuItem>
                    <MenuItem value="custom">2&quot; custom printed</MenuItem>
                    <MenuItem value="clear+custom">2&quot; clear + 2&quot; custom printed</MenuItem>
                  </Select>
                </FormControl>
                {index < allConnectors.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
            
            {Object.values(heatShrink).some((v) => v === 'clear' || v === 'clear+custom') && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Clear add-on is supplied unshrunk—you can add labels or shrink it yourself.
                </Typography>
              </Box>
            )}

            {Object.entries(heatShrink).some(
              ([k, v]) => k !== 'global' && (v === 'custom' || v === 'clear+custom')
            ) && (
              <CustomHeatShrinkPanel
                value={customHeatShrink}
                onChange={setCustomHeatShrink}
                sku={sku}
                customEndCount={countCustomHeatShrinkEnds({ heatShrink }, config)}
              />
            )}
          </Grid>

          {/* XLR Coding Rings Option - Only show if XLR connectors are present */}
          {hasXLRConnector() && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>
                  XLR Coding Rings
                </Typography>
                <Tooltip title="Color-coded rings for XLR connectors to help identify different channels or connections">
                  <HelpOutlineIcon color="action" fontSize="small" />
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose coding ring colors for each XLR connector. <i>(${codingRingsTotal.toFixed(2)} total)</i>
              </Typography>
              
              {xlrConnectors.map(({ key, position, connector }, index) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {getConnectorDisplayName(connector, position)}
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Coding Ring Color</InputLabel>
                    <Select
                      value={codingRings[key] || codingRings.global || 'none'}
                      onChange={(e) => handleCodingRingChange(key, e.target.value)}
                      label="Coding Ring Color"
                      MenuProps={{ disablePortal: true }}
                    >
                      {codingRingColors.map((color) => (
                        <MenuItem key={color.value} value={color.value}>
                          {color.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {index < xlrConnectors.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
              
              {codingRings.global !== 'none' && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Color-coded rings help identify different channels or connections.
                  </Typography>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
}

export default SimplifiedFinishingSelection;
